import { TrialWelcomeRoot } from '#~/account/components/settings/billing/trial-welcome-root.jsx';
import { useSettings } from '#~/account/components/hooks.js';
import type { ReactElement } from 'react';
import { twMerge } from 'cn';
import { useLayoutPref } from '../hooks.js';
import type { Props } from '../types.js';
import { Account } from './account/index.js';
import { Notifications } from './notifications/index.js';
import { Switcher } from './switcher/index.js';
import { Vendor, getCurrentVendor } from '@studio/design-system/themes/vendor.js';

/** `<Header>` component with responsive sidebars. */
export const Header = ({ children, right, className, center, ...props }: Props): ReactElement => {
  const settings = useSettings();

  const isIris = getCurrentVendor() === Vendor.IRIS;

  // Base styles
  const height = 'h-48';
  const passthru = 'flex items-center gap-12';

  const [layout] = useLayoutPref();
  const fixedWidth = layout.left.width;

  return (
    <div
      className={twMerge(
        // Base styles
        'grid items-center bg-base-100',
        // Grid layout
        `flex`,
        height,
        className
      )}
      {...props}
    >
      {/* Left sidebar */}
      <Switcher
        className={twMerge(height, !isIris ? 'min-w-full px-12' : 'min-w-full')}
        style={{ width: `${fixedWidth}px` }}
        settings={settings}
      />

      {/* Center content */}
      <div className='flex min-w-0 flex-grow items-center justify-center gap-12'>
        {center}
        <TrialWelcomeRoot />
      </div>

      {/* Right section with notifications and account */}
      <div
        className={twMerge(
          passthru,
          'flex items-center justify-end gap-12',
          'overflow-visible whitespace-nowrap',
          'px-4'
        )}
        style={{ width: `${fixedWidth}px` }}
      >
        {children}
        <div className='flex items-center justify-end space-x-[9.5px]'>
          {right !== undefined && right}
          <Notifications />
          <Account />
        </div>
      </div>
    </div>
  );
};

Header.displayName = 'Header';
